/**
 * Cargorun Lost Key Popup
 * Custom element that handles the "Codice Univoco Chiave" popup for products
 * that require it (based on the lost_key_richiedi_codice_univo metafield).
 */

if (!customElements.get('cargorun-lost-key-popup')) {
  class CargorunLostKeyPopup extends HTMLElement {
    constructor() {
      super();
      this.currentForm = null;
      this.currentSubmitEvent = null;
      this.boundKeyHandler = this.handleKeyDown.bind(this);
    }

    connectedCallback() {
      // Get DOM elements
      this.overlay = this.querySelector('.cargorun-lost-key-popup__overlay');
      this.modal = this.querySelector('.cargorun-lost-key-popup__modal');
      this.input = this.querySelector('#lost-key-codice-input');
      this.fieldWrapper = this.querySelector('.cargorun-lost-key-popup__field');
      this.confirmBtn = this.querySelector('[data-action="confirm"]');
      this.cancelBtn = this.querySelector('[data-action="cancel"]');
      this.closeBtn = this.querySelector('.cargorun-lost-key-popup__close');
      
      // Dynamic instructions elements
      this.instructionsContainer = this.querySelector('.cargorun-lost-key-popup__instructions');
      this.instructionsText = this.querySelector('.cargorun-lost-key-popup__instructions-text');
      this.instructionsImage = this.querySelector('.cargorun-lost-key-popup__instructions-image img');

      // Bind events
      this.bindEvents();
      
      // Listen for cart updates to clean up confirmed properties
      this.setupCartListener();
    }

    bindEvents() {
      // Close actions
      this.overlay?.addEventListener('click', () => this.hide());
      this.closeBtn?.addEventListener('click', () => this.hide());
      this.cancelBtn?.addEventListener('click', () => this.hide());

      // Confirm action
      this.confirmBtn?.addEventListener('click', () => this.handleConfirm());

      // Input enter key
      this.input?.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.handleConfirm();
        }
      });

      // Clear error on input
      this.input?.addEventListener('input', () => {
        this.clearError();
      });
    }

    /**
     * Setup listener to clean up property inputs after successful cart addition
     */
    setupCartListener() {
      // Listen for successful cart updates and remove the confirmed property inputs
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
          // Remove all confirmed property inputs so next add-to-cart asks again
          document.querySelectorAll('input[name="properties[Codice Univoco Chiave]"][data-confirmed]').forEach(input => {
            input.remove();
          });
        });
      }
    }

    /**
     * Show the popup modal
     * @param {HTMLFormElement} form - The product form that triggered the popup
     * @param {Event} submitEvent - The original submit event
     */
    show(form, submitEvent) {
      this.currentForm = form;
      this.currentSubmitEvent = submitEvent;

      // Reset state
      this.input.value = '';
      this.clearError();

      // Populate dynamic instructions from product-form data attributes
      this.populateInstructions(form);

      // Show popup immediately
      this.classList.add('active');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('cargorun-lost-key-popup-open');

      // Focus input immediately
      this.input?.focus();

      // Add escape key listener
      document.addEventListener('keydown', this.boundKeyHandler);
    }

    /**
     * Populate the dynamic instructions from product metafields
     * @param {HTMLFormElement} form - The product form element
     */
    populateInstructions(form) {
      // Find the parent product-form element to get the data attributes
      const productForm = form.closest('product-form');
      if (!productForm) {
        this.hideInstructions();
        return;
      }

      const description = productForm.dataset.lostKeyDescription || '';
      const imageUrl = productForm.dataset.lostKeyImage || '';

      // Check if we have any content to show
      if (!description && !imageUrl) {
        this.hideInstructions();
        return;
      }

      // Populate description (rich text - render as HTML)
      if (description && this.instructionsText) {
        // Decode HTML entities from the escaped rich text
        const decodedHtml = this.decodeHtmlEntities(description);
        this.instructionsText.innerHTML = decodedHtml;
        this.instructionsText.hidden = false;
      } else if (this.instructionsText) {
        this.instructionsText.innerHTML = '';
        this.instructionsText.hidden = true;
      }

      // Populate image
      if (imageUrl && this.instructionsImage) {
        this.instructionsImage.src = imageUrl;
        this.instructionsImage.parentElement.hidden = false;
      } else if (this.instructionsImage) {
        this.instructionsImage.src = '';
        this.instructionsImage.parentElement.hidden = true;
      }

      // Show the instructions container
      if (this.instructionsContainer) {
        this.instructionsContainer.hidden = false;
      }
    }

    /**
     * Hide the instructions container
     */
    hideInstructions() {
      if (this.instructionsContainer) {
        this.instructionsContainer.hidden = true;
      }
      if (this.instructionsText) {
        this.instructionsText.innerHTML = '';
      }
      if (this.instructionsImage) {
        this.instructionsImage.src = '';
      }
    }

    /**
     * Decode HTML entities from escaped string
     * @param {string} html - The escaped HTML string
     * @returns {string} - The decoded HTML string
     */
    decodeHtmlEntities(html) {
      const textarea = document.createElement('textarea');
      textarea.innerHTML = html;
      return textarea.value;
    }

    /**
     * Hide the popup modal
     */
    hide() {
      this.classList.remove('active');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('cargorun-lost-key-popup-open');

      // Remove escape key listener
      document.removeEventListener('keydown', this.boundKeyHandler);

      // Reset instructions
      this.hideInstructions();

      // Reset references
      this.currentForm = null;
      this.currentSubmitEvent = null;
    }

    /**
     * Handle keyboard events
     * @param {KeyboardEvent} e
     */
    handleKeyDown(e) {
      if (e.key === 'Escape') {
        this.hide();
      }
    }

    /**
     * Handle confirm button click
     */
    handleConfirm() {
      const value = this.input?.value?.trim();

      // Validate
      if (!value) {
        this.showError();
        this.input?.focus();
        return;
      }

      // Add the property to the form
      if (this.currentForm) {
        // Save form reference before hide() clears it
        const form = this.currentForm;
        
        this.addPropertyToForm(form, value);
        
        // Hide popup first
        this.hide();

        // Re-submit the form immediately using saved reference
        const submitButton = form.querySelector('[type="submit"]');
        if (submitButton) {
          submitButton.click();
        }
      }
    }

    /**
     * Add the Codice Univoco property to the form
     * @param {HTMLFormElement} form
     * @param {string} value
     */
    addPropertyToForm(form, value) {
      const propertyName = 'properties[Codice Univoco Chiave]';
      
      // Remove any existing input first
      const existingInput = form.querySelector(`input[name="${propertyName}"]`);
      if (existingInput) {
        existingInput.remove();
      }
      
      // Create hidden input with confirmed flag
      const hiddenInput = document.createElement('input');
      hiddenInput.type = 'hidden';
      hiddenInput.name = propertyName;
      hiddenInput.value = value;
      hiddenInput.dataset.confirmed = 'true'; // Mark as confirmed for this submission
      form.appendChild(hiddenInput);
    }

    /**
     * Show validation error
     */
    showError() {
      this.fieldWrapper?.classList.add('has-error');
      this.input?.setAttribute('aria-invalid', 'true');
    }

    /**
     * Clear validation error
     */
    clearError() {
      this.fieldWrapper?.classList.remove('has-error');
      this.input?.removeAttribute('aria-invalid');
    }
  }

  customElements.define('cargorun-lost-key-popup', CargorunLostKeyPopup);
}
